/**
 * RoundAvatar (KAN-618, extracted in KAN-857)
 *
 * Round picture from `src`, otherwise initials of the name. Presentational
 * only: the caller loads the picture (useAvatarSrc / useApiFileSrc) and passes
 * the ready src. A picture that fails to load falls back to the initials.
 * Used by SenderAvatar (our Telegram account) and ClientAvatar (the client).
 */
import { useState } from 'react'
import { nameInitials } from '@/lib/senderAccount'
import { cn } from '@/lib/utils'

const SIZE_CLASSES = {
  20: 'h-5 w-5 text-[9px]',
  24: 'h-6 w-6 text-[10px]',
  32: 'h-8 w-8 text-xs',
  40: 'h-10 w-10 text-sm',
  48: 'h-12 w-12 text-base',
} as const

export type RoundAvatarSize = keyof typeof SIZE_CLASSES

export interface RoundAvatarProps {
  /** Ready <img> src; null or "": no picture (yet). */
  src: string | null
  /** Name the initials are taken from when there is no picture. */
  name?: string
  /** Diameter in px. */
  size?: RoundAvatarSize
  className?: string
  testId?: string
  /** KAN-864: called once the picture fails to load (the initials are shown then). */
  onError?: (src: string) => void
}

export function RoundAvatar({ src, name = '', size = 20, className, testId, onError }: RoundAvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const shape = cn('shrink-0 rounded-full', SIZE_CLASSES[size], className)

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt=""
        data-testid={testId}
        className={cn(shape, 'object-cover')}
        onError={() => {
          setFailedSrc(src)
          onError?.(src)
        }}
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={cn(
        shape,
        'inline-flex items-center justify-center bg-gray-200 font-semibold leading-none text-gray-600'
      )}
    >
      {nameInitials(name)}
    </span>
  )
}
